import { SceneCommon, SceneFactory } from '@cgs/common';
import { LazyAction, NodeUtils } from '@cgs/shared';
import {
  Action,
  Container,
  FunctionAction,
  InterpolateCopyAction,
  InterpolateInplaceAction,
  IntervalAction,
  ParamEvent,
  SceneObject,
  SequenceSimpleAction,
  Tuple,
  Vector2,
} from '@cgs/wge';
import { ComponentNames } from '../../reels_engine/entity_components/component_names';
import { ISlotGameEngineProvider } from '../../reels_engine/game_components_providers/i_slot_game_engine_provider';
import { ISlotGame } from '../../reels_engine/i_slot_game';
import { ReelsEngine } from '../../reels_engine/reels_engine';
import {
  RESOURCES_COMPONENT_TOKEN,
  T_ISlotGame,
  T_ISlotGameEngineProvider,
} from '../../type_definitions';
import { IconTextHelper } from '../common/utils/icon_text_helper';
import { IFlyActionHelper } from './i_fly_action_helper';
import { ResourcesComponent } from './resources_component';

export class FlyActionHelper implements IFlyActionHelper {
  private _sceneFactory: SceneFactory;
  private _reelEngine: ReelsEngine;
  private _gameResourceProvider: ResourcesComponent;
  private _root: SceneObject;
  private _winHolder: SceneObject | null;
  private _animNode: SceneObject | null;

  constructor(container: Container, sceneCommon: SceneCommon) {
    this._sceneFactory = sceneCommon.sceneFactory;
    this._reelEngine =
      container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider).reelsEngine;
    this._gameResourceProvider = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this._root = container.resolve<ISlotGame>(T_ISlotGame).gameNode;

    const footer = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).footer;

    this._animNode = footer?.findById('glowAnimation') || null;
    this._winHolder = footer?.findById('TotalWinHolder') || null;
  }

  public collectSceneOnIconToHud(
    pos: number,
    value: number,
    options: {
      showValueOnScene?: boolean;
      isMultiplier?: boolean;
      featureSceneName?: string;
      textField?: string;
      featureFlyState?: string;
      duration?: number;
      valueString?: string;
    }
  ): Tuple<Action, Action> {
    if (!this._winHolder || !this._animNode) {
      throw new Error('Win holder or animation node not found in the HUD scene');
    }

    const destinationHolderPos = this.getDestinationMovingScenePosition(this._winHolder);
    const action = this.collectFromIconToMachineHolder(pos, destinationHolderPos, value, options);
    return new Tuple<Action, Action>(
      action.item1,
      new SequenceSimpleAction([
        new FunctionAction(() => {
          this._animNode!.stateMachine!.sendEvent(new ParamEvent<string>('anim'));
        }),
        action.item2,
      ])
    );
  }

  public getIconCenterPossitionOffset(): Vector2 {
    return new Vector2(
      this._reelEngine.internalConfig.symbolSize.x / 2,
      this._reelEngine.internalConfig.symbolSize.y / 2
    );
  }

  public flyAnimation(
    flyingNode: SceneObject,
    startPos: Vector2,
    endPos: Vector2,
    duration: number = 0.5
  ): Action {
    return new LazyAction(() => {
      const interpolateAction = new InterpolateInplaceAction<Vector2>((v) => v.clone())
        .withInterpolateFunction(Vector2.lerpInplace)
        .withValues(startPos, endPos)
        .withTimeFunction((time, _start, _dx) => time)
        .withDuration(duration);
      interpolateAction.valueChange.listen((e) => {
        flyingNode.position = e;
      });
      return interpolateAction;
    });
  }

  /**
   * Animates a node along a spiral path from start to end.
   * The radius decays towards the end position, producing a smooth spiral-in effect.
   */
  public flyAnimationSpiral(
    flyingNode: SceneObject,
    startPos: Vector2,
    endPos: Vector2,
    options?: {
      duration?: number;
      turns?: number;
      clockwise?: boolean;
      radiusScale?: number; // scales initial radius relative to distance start->end
      decay?: number; // radius decay power; 1 = linear, 2 = quadratic, 0 = constant
      easing?: (t: number) => number; // optional easing over [0..1]
      startPhase?: number; // radians to offset initial angle
    }
  ): Action {
    return new LazyAction(() => {
      const totalDuration = options?.duration ?? 0.8;
      const numberOfTurns = Math.max(0, options?.turns ?? 2);
      const isClockwise = options?.clockwise ?? true;
      const radiusScale = options?.radiusScale ?? 1.0;
      const decayPower = options?.decay ?? 1.0;
      const startPhase = options?.startPhase ?? 0.0;

      const relativeStart = new Vector2(startPos.x - endPos.x, startPos.y - endPos.y);
      const initialRadius = Math.hypot(relativeStart.x, relativeStart.y) * radiusScale;
      const initialAngle = Math.atan2(relativeStart.y, relativeStart.x) + startPhase;
      const directionSign = isClockwise ? -1 : 1;

      const interpolateScalar = (start: number, end: number, t: number): number =>
        start + (end - start) * t;

      const progressAction = new InterpolateCopyAction<number>()
        .withInterpolateFunction(interpolateScalar)
        .withValues(0, 1)
        .withTimeFunction((time, _start, _dx) => time)
        .withDuration(totalDuration);

      progressAction.valueChange.listen((progress: number) => {
        const normalizedTime = options?.easing ? options.easing(progress) : progress;
        const currentRadius = initialRadius * Math.pow(1 - normalizedTime, decayPower);
        const currentAngle =
          initialAngle + directionSign * 2 * Math.PI * numberOfTurns * normalizedTime;

        const x = endPos.x + Math.cos(currentAngle) * currentRadius;
        const y = endPos.y + Math.sin(currentAngle) * currentRadius;
        flyingNode.position = new Vector2(x, y);
      });

      return progressAction;
    });
  }

  public getDestinationMovingScenePosition(destinationHolder: SceneObject): Vector2 {
    const destination = new Vector2(
      destinationHolder.worldTransform.tx,
      destinationHolder.worldTransform.ty
    );
    this._root.inverseTransform.transformVectorInplace(destination);
    return destination;
  }

  public getStartMovingScenePosition(positionOnReels: number, offset: Vector2): Vector2 {
    const reelIndex = positionOnReels % this._reelEngine.ReelConfig.reelCount;
    const lineIndex = Math.floor(positionOnReels / this._reelEngine.ReelConfig.reelCount);
    const positionIndex = this._reelEngine.entityEngine.getComponentIndex(ComponentNames.Position);
    const entity = this._reelEngine.getReelStoppedEntities(reelIndex, lineIndex)[0];
    const entityPosition = entity.get(positionIndex) as Vector2;
    const animIconsHolder = this._gameResourceProvider.slot.findById(
      'anim_icons_holder'
    ) as SceneObject;
    const animIconsHolderPosition = new Vector2(
      animIconsHolder.worldTransform.tx,
      animIconsHolder.worldTransform.ty
    );
    this._gameResourceProvider.root.inverseTransform.transformVectorInplace(
      animIconsHolderPosition
    );
    return animIconsHolderPosition.add(entityPosition).add(offset);
  }

  public collectFromHolderToMachineHolder(
    startScene: SceneObject,
    destinationScene: SceneObject,
    value: number,
    options: {
      showValueOnScene?: boolean;
      isMultiplier?: boolean;
      featureSceneName?: string;
      textField?: string;
      featureFlyState?: string;
    }
  ): Tuple<Action, Action> {
    const iconActions: Action[] = [];
    const prizeScene = this.loadSceneToSlot(options.featureSceneName!, true, 100000);
    const position = this.getDestinationMovingScenePosition(startScene);
    const endPos = this.getDestinationMovingScenePosition(destinationScene);
    iconActions.push(
      new FunctionAction(() => {
        if (options.showValueOnScene) {
          if (options.isMultiplier) {
            IconTextHelper.updateTextOnIcon(prizeScene, value.toFixed(0), options.textField);
          } else {
            IconTextHelper.updateTextOnIcon(
              prizeScene,
              IconTextHelper.formatCoinsOnIcon(value, 4),
              options.textField
            );
          }
        }
        this.addCreatedSceneToSlot(prizeScene, position);
        prizeScene.stateMachine!.sendEvent(new ParamEvent<string>(options.featureFlyState!));
      })
    );
    iconActions.push(
      this.flyAnimation(
        prizeScene,
        position,
        endPos,
        (prizeScene.stateMachine!.findById(options.featureFlyState!)!.enterAction as IntervalAction)
          ?.duration ?? 0.0
      )
    );
    const afterFlyActions: Action[] = [];
    afterFlyActions.push(this.removeCreatedSceneFromSlotAction(prizeScene));
    return new Tuple<Action, Action>(
      new SequenceSimpleAction(iconActions),
      new SequenceSimpleAction(afterFlyActions)
    );
  }

  public collectFromIconToMachineHolder(
    pos: number,
    destinationHolderPos: Vector2,
    value: number,
    options: {
      showValueOnScene?: boolean;
      isMultiplier?: boolean;
      featureSceneName?: string;
      textField?: string;
      featureFlyState?: string;
      valueString?: string;
      duration?: number;
    }
  ): Tuple<Action, Action> {
    const iconActions: Action[] = [];
    const prizeScene = this.loadSceneToSlot(options.featureSceneName!, true, 100000);
    const position = this.getStartMovingScenePosition(pos, this.getIconCenterPossitionOffset());
    const endPos = destinationHolderPos;
    iconActions.push(
      new FunctionAction(() => {
        if (options.showValueOnScene) {
          if (options.valueString) {
            IconTextHelper.updateTextOnIcon(prizeScene, options.valueString, options.textField);
          } else if (options.isMultiplier) {
            IconTextHelper.updateTextOnIcon(prizeScene, value.toFixed(0), options.textField);
          } else {
            IconTextHelper.updateTextOnIcon(
              prizeScene,
              IconTextHelper.formatCoinsOnIcon(value, 4),
              options.textField
            );
          }
        }
        this.addCreatedSceneToSlot(prizeScene, position);
        prizeScene.stateMachine!.sendEvent(new ParamEvent<string>(options.featureFlyState!));
      })
    );
    iconActions.push(
      this.flyAnimation(
        prizeScene,
        position,
        endPos,
        options.duration === 0.0
          ? ((
              prizeScene.stateMachine!.findById(options.featureFlyState!)!
                .enterAction as IntervalAction
            )?.duration ?? 0.0)
          : options.duration
      )
    );
    const afterFlyActions: Action[] = [];
    afterFlyActions.push(this.removeCreatedSceneFromSlotAction(prizeScene));
    return new Tuple<Action, Action>(
      new SequenceSimpleAction(iconActions),
      new SequenceSimpleAction(afterFlyActions)
    );
  }

  public collectFromIconToMachineHolderWithStartOffset(
    pos: number,
    destinationHolderPos: Vector2,
    startOffset: Vector2,
    value: number,
    options: {
      showValueOnScene?: boolean;
      isMultiplier?: boolean;
      drawOrder?: number;
      featureSceneName?: string;
      textField?: string;
      featureFlyState?: string;
      valueString?: string;
      parent?: SceneObject;
    }
  ): Tuple<Action, Action> {
    const iconActions: Action[] = [];
    const prizeScene = this.loadSceneToSlot(options.featureSceneName!, true, options.drawOrder);
    const position = this.getStartMovingScenePosition(pos, startOffset);
    const endPos = destinationHolderPos;
    iconActions.push(
      new FunctionAction(() => {
        if (options.showValueOnScene) {
          if (options.valueString) {
            IconTextHelper.updateTextOnIcon(prizeScene, options.valueString, options.textField);
          } else if (options.isMultiplier) {
            IconTextHelper.updateTextOnIcon(prizeScene, value.toFixed(0), options.textField);
          } else {
            IconTextHelper.updateTextOnIcon(
              prizeScene,
              IconTextHelper.formatCoinsOnIcon(value, 4),
              options.textField
            );
          }
        }
        this.addCreatedSceneToSlot(prizeScene, position, options.parent);
        prizeScene.stateMachine!.sendEvent(new ParamEvent<string>(options.featureFlyState!));
      })
    );
    iconActions.push(
      this.flyAnimation(
        prizeScene,
        position,
        endPos,
        (prizeScene.stateMachine!.findById(options.featureFlyState!)!.enterAction as IntervalAction)
          ?.duration ?? 0.0
      )
    );
    const afterFlyActions: Action[] = [];
    afterFlyActions.push(this.removeCreatedSceneFromSlotAction(prizeScene));
    return new Tuple<Action, Action>(
      new SequenceSimpleAction(iconActions),
      new SequenceSimpleAction(afterFlyActions)
    );
  }

  public loadSceneToSlot(
    sceneName: string,
    loadFromAdditional: boolean = false,
    drawOrder?: number
  ): SceneObject {
    let fsScene: SceneObject;
    if (loadFromAdditional) {
      fsScene = this._sceneFactory.build(sceneName)!;
    } else {
      fsScene = this._sceneFactory.build(sceneName)!;
    }
    if (typeof drawOrder === 'number') {
      fsScene.z = drawOrder;
    } else {
      fsScene.z = 100000;
    }
    fsScene.visible = false;
    fsScene = NodeUtils.removeMask(fsScene);
    fsScene.active = true;
    fsScene.initialize();
    return fsScene;
  }

  public addCreatedSceneToSlot(
    scene: SceneObject,
    initialPos: Vector2,
    parent: SceneObject | null = null
  ): void {
    if (!scene.isInitialized) {
      scene.initialize();
    }
    if (parent) {
      parent.addChild(scene);
    } else {
      this._root.addChild(scene);
    }
    scene.position = initialPos;
    scene.visible = true;
  }

  public removeCreatedSceneFromSlot(scene: SceneObject): void {
    scene.parent!.removeChild(scene);
    scene.removeAllChildsDisposing();
    scene.stateMachine?.sendEvent(new ParamEvent<string>('default'));
    scene.scale = new Vector2(1.0, 1.0);
    scene.visible = false;
    scene.deinitialize();
  }

  public addCreatedSceneToSlotAction(
    scene: SceneObject,
    initialPos: Vector2,
    parent: SceneObject | null = null
  ): Action {
    return new FunctionAction(() => this.addCreatedSceneToSlot(scene, initialPos, parent));
  }

  public removeCreatedSceneFromSlotAction(scene: SceneObject): Action {
    return new FunctionAction(() => this.removeCreatedSceneFromSlot(scene));
  }
}
